// (C) Copyright 2025 Hewlett Packard Enterprise Development LP

import { Trash } from 'grommet-icons'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { Anchor, DataTable, Button } from '../../../../components'
import VisibilityWrapper from '../../../../commoncomponents/visibility-wrapper/VisibilityWrapper'
import { isAuthzV2London } from '../../../../utils/feature-flag-utils'
import { getMultiScopeColumns, getEditRABtnColumn } from '../utils'
import { roleAssignmentEnum, subjectTypesEnum } from '../constants'

const RoleAssignmentContent = ({
  handleOnDeleteBtnClick,
  handleOnEditRA = () => {},
  retrievedData,
  subjectType
}) => {
  const navigate = useNavigate()
  const LDFlags = useFlags()
  const { t } = useTranslation(['iam', 'common'])

  const handleRowClickForRASource = (datum) => {
    navigate(`/manage-account/identity/groups/${datum?.source_id}`)
  }
  const handleRowClickForRARole = (datum) => {
    navigate(
      `/manage-account/identity/roles/${encodeURIComponent(datum?.role_grn)}`
    )
  }
  const isV2London = isAuthzV2London(LDFlags)

  return (
    <>
      <DataTable
        grid={{
          columns: [
            {
              header: t('common:business_object.role_capitalized'),
              property: 'meta.role',
              size: 'small',
              sortable: false,
              render: (datum) => {
                return (
                  <Anchor
                    href="#"
                    label={datum?.role_name}
                    testId="users-role-assignment-anchor"
                    onClick={(event) => {
                      event.preventDefault()
                      handleRowClickForRARole(datum)
                    }}
                  />
                )
              }
            },
            {
              header: t('iam:roles_table_headers.service'),
              property: 'meta.service',
              sortable: false,
              render: (datum) => {
                return datum?.service_name || datum?.custom_role_service_name
              }
            },
            ...getMultiScopeColumns(isV2London, t, navigate),
            ...(subjectType === subjectTypesEnum?.USER
              ? [
                  {
                    header: t('iam:role_assignment.source_assignment_title'),
                    property: 'meta.source',
                    sortable: false,
                    render: (datum) => {
                      if (
                        datum?.source === roleAssignmentEnum?.DIRECT ||
                        datum?.source === roleAssignmentEnum?.EXTERNAL ||
                        datum?.source === roleAssignmentEnum?.SSO
                      ) {
                        return datum?.source === roleAssignmentEnum?.EXTERNAL
                          ? t('common:external')
                          : datum?.source
                      }
                      return (
                        <Anchor
                          href="#"
                          label={datum?.source}
                          testId="users-role-assignment-anchor"
                          onClick={() => handleRowClickForRASource(datum)}
                        />
                      )
                    }
                  }
                ]
              : []),
            ...getEditRABtnColumn(isV2London, handleOnEditRA),
            {
              header: '',
              property: t('iam:users.delete'),
              sortable: false,
              render: (datum) => {
                if (datum?.source === 'Direct' || datum?.source === undefined) {
                  return (
                    <VisibilityWrapper
                      rbac={{
                        resource: '/ccs/authorization',
                        permission: 'ccs.authorization.edit'
                      }}
                    >
                      <Button
                        icon={<Trash />}
                        onClick={() => handleOnDeleteBtnClick(datum)}
                        tip={t(
                          'iam:role_assignment.remove_assignment_aria_label'
                        )}
                        testId="role-assignments-table-data-delete"
                        a11yTitle={t(
                          'iam:role_assignment.remove_assignment_aria_label'
                        )}
                      />
                    </VisibilityWrapper>
                  )
                }
                return null
              }
            },
            {
              property: 'id',
              primary: true,
              render: () => <></>
            }
          ],
          data: retrievedData
        }}
        testId="role-assignments-data-table"
      />
    </>
  )
}

RoleAssignmentContent.propTypes = {
  handleOnDeleteBtnClick: PropTypes.func.isRequired,
  handleOnEditRA: PropTypes.func,
  retrievedData: PropTypes.array.isRequired,
  subjectType: PropTypes.string.isRequired
}

export default RoleAssignmentContent
